import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/pages/HelpPage.css';

const topics = [
  {
    id: 'bookings', title: 'Bookings', description: 'From the first conversation to a confirmed booking.',
    questions: [
      ['How do I book a photographer?', <>
        <p><Link to="/">Find a photographer</Link>, explore their profile and portfolio, then choose <strong>Message</strong> to discuss your shoot. You need to log in or create an account to send messages.</p>
        <p>Once you have agreed the details, the photographer uses <strong>Create booking</strong> in your conversation to send a proposal with the date, time, location, price and payment terms. Review the proposal, then choose <strong>Accept &amp; pay</strong> to continue to payment.</p>
      </>],
      ['When is a booking confirmed?', <>
        <p>A booking becomes <strong>Confirmed</strong> after ViewFinders receives confirmation of the first successful payment from Stripe. This can be the full payment or a deposit, depending on the proposal.</p>
        <p>Accepting a proposal alone does not confirm it. If you pay a deposit, the booking can be confirmed while the remaining balance is still outstanding. Check the booking card in your conversation for its current status and payment breakdown.</p>
      </>],
      ['Can a photographer change a booking proposal?', <>
        <p>Yes, while an unpaid proposal still has a <strong>Revise</strong> option in the conversation. The photographer can update the booking details and payment terms; you then need to review and accept the revised proposal again.</p>
        <p>The revision option is not available while the booking is awaiting payment or after it has been confirmed. If you need to discuss a change, message the photographer.</p>
      </>],
      ['What happens if I decline a proposal?', <>
        <p>Choosing <strong>Decline</strong> marks that proposal as declined and releases the time held for it in the photographer’s calendar. The proposal does not become a confirmed booking.</p>
        <p>Your conversation remains available. You can keep discussing the shoot, and the photographer can send a new proposal if you agree different details.</p>
      </>],
    ],
  },
  {
    id: 'payments', title: 'Payments', description: 'Understand the payment terms in your proposal.',
    questions: [
      ['How do payments work?', <>
        <p>Each booking proposal shows the total price and payment terms. Accepting it takes you to Stripe Checkout to make the first payment. ViewFinders updates the booking and its payment breakdown when Stripe confirms the payment.</p>
        <p>If you leave checkout without completing payment, return to the booking card in your conversation to check its status and use the payment option to try again.</p>
      </>],
      ['Can I pay a deposit and the balance later?', <>
        <p>Yes, if the photographer includes deposit terms in the proposal. They choose the deposit percentage and how many days before the shoot the balance is due. You can review the amounts and balance due date in the booking card before accepting.</p>
        <p>After paying the deposit, return to that card to pay the remaining balance. If the proposal asks for full payment instead, discuss different terms with the photographer before accepting.</p>
      </>],
      ['When will I be asked to pay?', <>
        <p>The first payment is part of accepting the proposal: either the full amount or the deposit shown. Any remaining balance and its due date appear in the same booking card, with a <strong>Pay balance</strong> option after the deposit is paid.</p>
        <p>You make each payment through checkout. Use the payment terms in your conversation to keep track of what is still due.</p>
      </>],
      ['Who processes my payment?', <p>Payments are processed through <strong>Stripe Checkout</strong>. ViewFinders takes you to Stripe to complete payment, then uses Stripe’s confirmation to update the booking. The update may take a moment to appear in your conversation.</p>],
    ],
  },
  {
    id: 'messages', title: 'Messages', description: 'Discuss a shoot and keep the details together.',
    questions: [
      ['How do I contact a photographer?', <p>Open their profile and choose <strong>Message</strong>. Log in or create an account if prompted, then send your message. Share the date, location and kind of photography you have in mind so you can discuss the details before a proposal is sent.</p>],
      ['Where can I see my previous conversations?', <p>Open the account menu using your profile picture or initial in the navigation bar, then choose <Link to="/messages">Messages</Link>. Select a conversation to see its messages and booking proposals. Photographers can also open recent conversations from their home dashboard.</p>],
      ['How will I know when I receive a new message?', <>
        <p>While you are signed in and connected, new messages can appear in chat. An unread-message indicator on your account menu helps you spot conversations to check.</p>
        <p>Open <Link to="/messages">Messages</Link> when you return to the site to see your conversations and replies.</p>
      </>],
    ],
  },
  {
    id: 'availability', title: 'Availability', description: 'Use the calendar as a starting point for a conversation.',
    questions: [
      ['How do photographers set their availability?', <>
        <p>Photographers can manage <strong>Your availability</strong> on their home dashboard or their own profile. Select a day, then drag across a time range to mark it unavailable. Select a manual block to remove it after confirming.</p>
        <p>Booking times are added through the booking flow and managed with the booking, rather than deleted directly from the calendar. Calendar times are shown in UK time.</p>
      </>],
      ['Does an available date guarantee a booking?', <p>No. The calendar shows availability based on the times currently recorded. Message the photographer to agree the shoot details. A booking is only confirmed once a proposal has been accepted and the first payment has succeeded.</p>],
    ],
  },
  {
    id: 'profiles', title: 'Profiles & portfolios', description: 'Show your work and help clients get to know you.',
    questions: [
      ['How do photographers update their profile?', <p>Open <strong>Your profile</strong> from the account menu, then choose <strong>Edit Profile</strong>. You can change your profile picture, cover photo, bio, specialities, travel details and packages. Choose <strong>Save Changes</strong> to save your updates. Selected photos preview in the editor; uploaded images may take a little time to finish processing.</p>],
      ['How do portfolios work?', <>
        <p>A photographer’s profile displays their photos grouped into albums. Choose an album to browse its images, then select a photo to see it at a larger size.</p>
        <p>On your own profile, choose <strong>Upload</strong> in <strong>Your Portfolio</strong>. Select up to ten JPEG, PNG or WebP images and optionally enter an album name. Photos uploaded without an album name go into <strong>Uncategorized</strong>. New photos appear after upload and processing.</p>
      </>],
    ],
  },
  {
    id: 'support', title: 'Support', description: 'Get in touch if you need a hand.',
    questions: [
      ['How do I contact ViewFinders?', <p>Use the <Link to="/contact">contact form</Link> and choose <strong>Support</strong> as the subject. Include your name, email address and a description of what you need help with. For questions about a particular shoot, message the photographer in your conversation.</p>],
    ],
  },
];

export default function HelpPage() {
  return (
    <main className="help-page">
      <header className="help-hero">
        <p className="help-eyebrow">Help &amp; guidance</p>
        <h1>How ViewFinders works</h1>
        <p className="help-intro">A practical guide to finding a photographer, agreeing a booking and managing your work on ViewFinders.</p>
        <ol className="help-steps" aria-label="The booking journey">
          <li><span aria-hidden="true">01</span><div><strong>Find your photographer</strong><p>Explore profiles and portfolios.</p></div></li>
          <li><span aria-hidden="true">02</span><div><strong>Talk through the details</strong><p>Agree your shoot in messages.</p></div></li>
          <li><span aria-hidden="true">03</span><div><strong>Review, accept &amp; pay</strong><p>Confirm through a booking proposal.</p></div></li>
        </ol>
      </header>

      <div className="help-layout">
        <nav className="help-topics" aria-label="Help topics">
          <p>On this page</p>
          <ul>{topics.map(({ id, title }) => <li key={id}><a href={`#${id}`}>{title}</a></li>)}</ul>
          <Link className="help-contact-link" to="/contact">Contact ViewFinders <span aria-hidden="true">↗</span></Link>
        </nav>

        <div className="help-sections">
          {topics.map(({ id, title, description, questions }) => (
            <section className="help-section" id={id} key={id} aria-labelledby={`${id}-heading`}>
              <h2 id={`${id}-heading`}>{title}</h2>
              <p className="help-section-intro">{description}</p>
              <div className="help-questions">
                {questions.map(([question, answer]) => (
                  <details className="help-question" key={question}>
                    <summary>{question}<span className="help-toggle" aria-hidden="true" /></summary>
                    <div className="help-answer">{answer}</div>
                  </details>
                ))}
              </div>
            </section>
          ))}
          <div className="help-next-step">
            <div><h2>Ready to explore?</h2><p>Start with the photographers and work that catch your eye.</p></div>
            <Link to="/">Find photographers <span aria-hidden="true">→</span></Link>
          </div>
        </div>
      </div>
    </main>
  );
}
